import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../api/tourism';

export interface StateGrouped {
  ladakh: any[];
  kashmir: any[];
  himachal: any[];
}

interface HeaderItineraries {
  destinationItineraries: StateGrouped;
  trekkingItineraries: StateGrouped;
  bikeItineraries: StateGrouped;
  loading: boolean;
}

function getStateKey(value: string): 'ladakh' | 'kashmir' | 'himachal' | null {
  const v = value.toLowerCase();
  if (v.includes('ladakh') || v.includes('zanskar')) return 'ladakh';
  if (v.includes('kashmir') || v.includes('jammu')) return 'kashmir';
  if (v.includes('himachal')) return 'himachal';
  return null;
}

function extractState(stateField: any): string {
  if (Array.isArray(stateField)) return stateField[0] || '';
  if (typeof stateField === 'string') return stateField;
  return '';
}

function extractTrekState(region: string): string {
  if (!region) return '';
  // "Zanskar, Ladakh, India" → check each part
  const parts = region.split(',').map(p => p.trim());
  for (const part of parts) {
    const key = getStateKey(part);
    if (key) return part;
  }
  return region;
}

export function useHeaderItineraries(): HeaderItineraries {
  const [destinationItineraries, setDestinationItineraries] = useState<StateGrouped>({ ladakh: [], kashmir: [], himachal: [] });
  const [trekkingItineraries, setTrekkingItineraries] = useState<StateGrouped>({ ladakh: [], kashmir: [], himachal: [] });
  const [bikeItineraries, setBikeItineraries] = useState<StateGrouped>({ ladakh: [], kashmir: [], himachal: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHeaderData = async () => {
      try {
        // Add cache-busting timestamp to force fresh data
        const timestamp = Date.now();
        const [destRes, treksRes, bikesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/v1/itineraries?t=${timestamp}`, { cache: 'no-store' }),
          fetch(`${API_BASE_URL}/api/v1/itineraries/treks?t=${timestamp}`, { cache: 'no-store' }),
          fetch(`${API_BASE_URL}/api/v1/itineraries/bikes?t=${timestamp}`, { cache: 'no-store' }),
        ]);

        // Destinations — categorize by state (deduplicate by id)
        if (destRes.ok) {
          const data = await destRes.json();
          const all = data.itineraries || [];
          const grouped: StateGrouped = { ladakh: [], kashmir: [], himachal: [] };
          const seen = new Set<string>();
          all.forEach((it: any) => {
            const uid = it.id || it.title || it.name;
            if (seen.has(uid)) return;
            seen.add(uid);
            const key = getStateKey(extractState(it.state));
            if (key) grouped[key].push(it);
          });
          setDestinationItineraries(grouped);
        }

        // Treks — categorize by region (deduplicate by id)
        if (treksRes.ok) {
          const data = await treksRes.json();
          const treks = data.treks || [];
          const grouped: StateGrouped = { ladakh: [], kashmir: [], himachal: [] };
          const seen = new Set<string>();
          treks.forEach((trek: any) => {
            const uid = trek.id || trek.trekName || trek.name;
            if (seen.has(uid)) return;
            seen.add(uid);
            const stateVal = extractTrekState(trek.region || '');
            const key = getStateKey(stateVal);
            if (key) grouped[key].push(trek);
          });
          setTrekkingItineraries(grouped);
        }

        // Bikes — categorize by state (deduplicate by id)
        if (bikesRes.ok) {
          const data = await bikesRes.json();
          const bikes = data.bikes || [];
          const grouped: StateGrouped = { ladakh: [], kashmir: [], himachal: [] };
          const seen = new Set<string>();
          bikes.forEach((bike: any) => {
            const uid = bike.id || bike.title || bike.name;
            if (seen.has(uid)) return;
            seen.add(uid);
            const key = getStateKey(extractState(bike.state));
            if (key) grouped[key].push(bike);
          });
          setBikeItineraries(grouped);
        }
      } catch (error) {
        console.error('Error fetching header itinerary data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHeaderData();
  }, []);

  return { destinationItineraries, trekkingItineraries, bikeItineraries, loading };
}
